class ScrollToHandler {
  constructor() {
    this.init();
  }

  init() {
    this.attachScrollListeners();
  }

  attachScrollListeners() {
    const scrollLinks = document.querySelectorAll('.scroll-to-link');
    
    scrollLinks.forEach(link => {
      if (this.isValidScrollLink(link)) {
        link.addEventListener('click', this.handleScrollClick.bind(this));
      }
    });
  }

  isValidScrollLink(link) {
    const href = link.getAttribute('href');
    return href && href.startsWith('#') && href.length > 1;
  }

  handleScrollClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const link = event.currentTarget;
    const targetId = link.getAttribute('href').substring(1);
    const targetElement = this.findTargetElement(targetId);

    if (targetElement) {
      // Small delay to ensure layout is stable
      requestAnimationFrame(() => {
        this.scrollToElement(targetElement);
      });
    } else {
      console.warn(`ScrollTo: Target element not found for ID: ${targetId}`);
    }
  }

  findTargetElement(targetId) {
    let target = document.getElementById(targetId);
    
    if (!target) {
      target = document.querySelector(`[data-scroll-target="${targetId}"]`);
    }

    if (!target) {
      target = document.querySelector(`[id="${targetId}"]`);
    }

    return target;
  }

  scrollToElement(element) {
    const offset = this.getScrollOffset();
    
    // Add visual feedback during scroll
    this.addScrollingFeedback();
    
    // Always use our custom smooth scroll for consistent animation
    this.smoothScrollPolyfill(element, offset);
  }

  addScrollingFeedback() {
    // Add a subtle loading indicator during scroll
    document.body.style.cursor = 'wait';
    document.body.classList.add('is-scrolling');
    
    // Remove feedback after animation
    setTimeout(() => {
      document.body.style.cursor = '';
      document.body.classList.remove('is-scrolling');
    }, 1200);
  }

  supportsSmoothScrolling() {
    return 'scrollBehavior' in document.documentElement.style;
  }

  smoothScrollPolyfill(element, offset) {
    const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = Math.max(Math.min(Math.abs(distance) * 1.2, 1200), 600); // 600ms to 1200ms
    let start = null;

    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const ease = this.easeInOutQuart(progress);
      
      window.scrollTo(0, startPosition + distance * ease);
      
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  easeInOutQuart(t) {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
  }

  getScrollOffset() {
    const headerElement = document.querySelector('header');
    let offset = 20;

    if (headerElement) {
      const headerHeight = headerElement.offsetHeight;
      const headerStyle = window.getComputedStyle(headerElement);
      const isFixed = headerStyle.position === 'fixed' || headerStyle.position === 'sticky';
      
      if (isFixed) {
        offset = headerHeight + 20;
      }
    }

    return offset;
  }

  reinitialize() {
    this.attachScrollListeners();
  }
}

// Initialize scroll handler
const initializeScrollTo = () => {
  if (typeof window !== 'undefined') {
    const scrollToHandler = new ScrollToHandler();
    
    // Re-initialize when new content is loaded
    document.addEventListener('shopify:section:load', () => {
      scrollToHandler.reinitialize();
    });
    
    return scrollToHandler;
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeScrollTo);
} else {
  initializeScrollTo();
}

// Module exports
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ScrollToHandler;
}

// Global access
window.ScrollToHandler = ScrollToHandler;
